/* istanbul ignore file */
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import Button from 'reactstrap/lib/Button';
import classnames from 'classnames';
import { get, flatten, compact } from 'lodash';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import {
  buildEvTestedDataPath,
  buildFuelCostsPath,
  FeatureSpecsEntities,
  FeatureSpecsPaths,
  VehicleEntities,
  VehicleModel,
} from 'client/data/models/vehicle-v2';
import { VisitorEntities } from 'client/data/models/visitor';
import { FeaturesSpecsParams } from 'site-modules/shared/utils/features-specs/params';
import { EV_TESTED_DATA_START_DATE } from 'site-modules/shared/constants/range-and-cost';
import { COLORS_FEATURES, CUSTOM_MAPPING } from 'site-modules/features-specs/constants/features-specs';
import { IncentivesAd } from 'site-modules/shared/components/native-ad/incentives-ad/incentives-ad';
import { BuildPriceAd } from 'site-modules/shared/components/native-ad/buildprice-ad/buildprice-ad';
import { SponsoredCars } from 'site-modules/shared/components/core-page/sponsored-cars/sponsored-cars';
import { NativeAd } from 'site-modules/shared/components/ad-unit/native-ad';
import { MrectNative } from 'site-modules/shared/components/native-ad/mrect/mrect-native';
import {
  getCustomMapFeatures,
  getOverviewMap,
  getSectionAnchor,
  includeAdditionalFeatures,
  isMatchKeyword,
} from 'site-modules/features-specs/utils/features-specs';
import { FeaturesSection } from 'site-modules/features-specs/components/features-section/features-section';
import { InventorySection } from 'site-modules/features-specs/components/inventory-section/inventory-section';
import { isUsed as isUsedPubState } from 'site-modules/shared/utils/publication-states';
import { TrackCarValueModule } from 'site-modules/shared/components/track-car-value/track-car-value-module';
import { KeywordInput } from 'site-modules/features-specs/components/keyword-input/keyword-input';
import { InsightAd } from 'site-modules/shared/components/native-ad/insight-ad/insight-ad';
import { AnchorNavContent } from 'site-modules/shared/components/anchor-nav-sticky/anchor-nav-content';
import { MiniInventoryModule } from 'site-modules/shared/components/mini-inventory-module/mini-inventory-module';

import './features-specs-table.scss';

function FeaturesSpecsTableAboveFoldUI({ params, styleOverview, isMobile }) {
  const { styleAttributes } = styleOverview;
  const featuresSectionProps = { params, isMobile, style: styleOverview };

  return (
    <FeaturesSection
      {...featuresSectionProps}
      category="Overview"
      features={getCustomMapFeatures(styleOverview, getOverviewMap(styleAttributes))}
      isFirstSection
    />
  );
}

FeaturesSpecsTableAboveFoldUI.propTypes = {
  params: FeaturesSpecsParams.isRequired,
  isMobile: PropTypes.bool.isRequired,
  styleOverview: FeatureSpecsEntities.FeatureStyleOverviewEntity,
};

FeaturesSpecsTableAboveFoldUI.defaultProps = {
  styleOverview: null,
};

const FeaturesSpecsTableAboveFold = connectToModel(FeaturesSpecsTableAboveFoldUI, {
  styleOverview: bindToPath(props => {
    const { styleId } = props; // user selection || page url || default style || undefined if parent component has not resolved popular styles yet
    if (styleId) {
      return FeatureSpecsPaths.buildStyleFeaturesOverviewPath(styleId);
    }
    return FeatureSpecsPaths.buildFeaturesOverviewPathForDefaultStyle(props.params);
  }, VehicleModel),
});

const KEYWORD_CREATIVE_ID = 'feature_search';

function FeaturesSpecsTableBelowFoldUI({
  vehicle,
  params,
  style,
  trimName,
  fuelCostsData,
  evTestedData,
  visitorLocation,
  pageName,
  isMobile,
}) {
  const [keyword, setKeyword] = useState('');

  function clearKeyword() {
    setKeyword('');
  }
  const styleId = style?.id;
  const isElectric = style?.styleAttributes?.electric;

  const { filteredOrderedFeatures, filteredOrderedFeaturesNames, navList } = useMemo(
    () => {
      if (!styleId) {
        return {};
      }
      const additionalCategoryMap = {
        Engine: {
          category: 'Towing & Hauling',
          features: getCustomMapFeatures(style, CUSTOM_MAPPING['Towing & Hauling']),
        },
        Measurements: {
          category: 'Colors',
          features: COLORS_FEATURES,
        },
      };

      const orderedFeatures = get(style, 'orderedFeatures', []).map(section => ({
        ...section,
        features: section.features.filter(({ name }) => isMatchKeyword(name, keyword)),
        additionalCategory: {
          ...additionalCategoryMap[section.category],
          features: additionalCategoryMap[section.category]?.features?.filter(({ name }) =>
            isMatchKeyword(name, keyword)
          ),
          hasCategory: !!additionalCategoryMap[section.category]?.features?.length,
        },
      }));

      const filteredFeatures = orderedFeatures.filter(
        ({ features, additionalCategory }) => !!features.length || !!additionalCategory?.features?.length
      );

      const anchorNavList = flatten(
        orderedFeatures.map(({ category, additionalCategory }) =>
          compact([
            getSectionAnchor(category, isElectric),
            additionalCategory?.hasCategory && getSectionAnchor(additionalCategory.category, isElectric),
          ])
        )
      );

      return {
        navList: anchorNavList,
        filteredOrderedFeatures: filteredFeatures,
        filteredOrderedFeaturesNames: flatten(filteredFeatures.map(({ features }) => features))
          .map(({ name }) => name)
          .join(),
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [styleId, isElectric, keyword, isMobile]
  );

  useEffect(() => {
    if (!keyword) {
      return;
    }

    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        action_category: TrackingConstant.USER_ACTION_CATEGORY,
        action_name: TrackingConstant.VIEW_SEARCH_RESULT,
        subaction_name: filteredOrderedFeaturesNames
          ? TrackingConstant.VIEW_AUTO_COMPLETE_RESULT
          : TrackingConstant.EMPTY_AUTO_COMPLETE_RESULT,
        action_cause: TrackingConstant.ACTION_CAUSE_LINK_CLICK,
        creative_id: KEYWORD_CREATIVE_ID,
        input: keyword,
        autocomplete_results: filteredOrderedFeaturesNames || undefined,
      },
    });
  }, [keyword, filteredOrderedFeaturesNames]);

  if (!style) {
    return null;
  }

  const featuresSectionProps = { params, isMobile, style };

  const isUsed = isUsedPubState(vehicle.pubStates);

  const clearFilterButton = (
    <Button
      size="lg"
      color="link"
      className="clear-btn text-underline mb-2 text-transform-none p-0 text-blue-50 fw-normal"
      onClick={clearKeyword}
    >
      Clear filter
    </Button>
  );

  return (
    <Fragment>
      {navList?.length > 1 && (
        <AnchorNavContent
          navList={navList}
          isMobile={isMobile}
          className="w-100 mb-1_5"
          listClassName="d-flex align-items-end"
          activeClass="link-active text-blue-40 fw-medium"
          linkTrackingId="view_specs_non_docked"
          creativeId="features_specs_sub-nav"
        />
      )}
      <Row className="mt-1_5">
        <Col xs={12} md={8} lg={7}>
          <KeywordInput
            className="mb-2"
            labelClassName="heading-5 mb-0_5"
            creativeId={KEYWORD_CREATIVE_ID}
            keyword={keyword}
            hasResults={!!filteredOrderedFeatures.length}
            setKeyword={setKeyword}
            clearKeyword={clearKeyword}
          />
          {!!keyword && (
            <Fragment>
              <div className="keyword-result fw-medium mb-0_25">
                {get(filteredOrderedFeatures, 'length') ? (
                  <Fragment>Showing results for &quot;{keyword}&quot;</Fragment>
                ) : (
                  <Fragment>No results for &quot;{keyword}&quot;</Fragment>
                )}
              </div>
              {clearFilterButton}
            </Fragment>
          )}
        </Col>
      </Row>

      {filteredOrderedFeatures.map(({ category, features, additionalCategory }, index) => {
        const key = `${category}-${index}`;

        const currentFeatures = includeAdditionalFeatures({
          features,
          category,
          fuelCostsData,
          evTestedData,
          isPluginElectric: get(style, 'styleAttributes.pluginElectric'),
        });

        return (
          <Fragment key={key}>
            {index === 1 && (
              <InsightAd
                mobile={isMobile}
                horizontalLayout
                wrapperClass="has-box-shadow mx-auto my-2"
                style={{ maxWidth: '752px' }}
              />
            )}
            <FeaturesSection
              {...featuresSectionProps}
              category={category}
              features={currentFeatures}
              keyword={keyword}
            />

            {category === 'Fuel' && (
              <MiniInventoryModule
                className="mb-3"
                vehicleParams={{
                  makeName: vehicle.make.name,
                  modelName: vehicle.model.name,
                  makeSlug: vehicle.make.slug,
                  modelSlug: vehicle.model.slug,
                  year: vehicle.year,
                }}
                isMobile={isMobile}
              />
            )}

            {category === 'Drive Train' && (
              <Container key="incentives-site-served-ad">
                <Row>
                  <Col
                    xs={12}
                    className="features-section bg-cool-gray-90 rounded-12 px-1 px-md-1_5 py-1_5 mb-1 mb-md-1_5"
                  >
                    <IncentivesAd mobile={isMobile} includeMrectAdCall wrapperClass="w-100" isFeaturesSpecsLayout />
                  </Col>
                </Row>
              </Container>
            )}
            {category === 'Safety' && (
              <BuildPriceAd
                mobile={isMobile}
                key="build-price-site-served-ad"
                wrapperClass="mb-3 d-flex justify-content-center"
                includeMrectAdCall={!isUsed}
              />
            )}
            {category === 'Interior Options' && (
              <div data-test="sponsored-cars-container" key="sponsored-cars-container">
                <SponsoredCars wrapperClassName="mt-2 mt-md-0" showBottomDividingLine={false} />
              </div>
            )}
            {category === 'Frontseats' && (
              <div data-test="native-mrect1" key="native-mrect1">
                <NativeAd
                  wrapperClass="mrect mt-2 mt-md-0 mb-3 mx-auto"
                  adNameWired="mrect"
                  adNameMobile="mmrect"
                  nativeStyle="mediumrectangle"
                  showLabel
                  verticalOffset="100%"
                  renderWhenViewable
                  useMinHeight
                />
              </div>
            )}
            {category === 'Tires and Wheels' && (
              <div data-test="native-mrect2" key="native-mrect2">
                <MrectNative
                  position={'2'}
                  wiredOnly
                  wrapperClass="mrect2 mb-3 hidden-sm-down"
                  verticalOffset="100%"
                  renderWhenViewable
                  useMinHeight
                />
              </div>
            )}
            {!!additionalCategory?.features?.length && (
              <FeaturesSection
                {...featuresSectionProps}
                category={additionalCategory.category}
                features={additionalCategory.features}
                keyword={keyword}
              />
            )}
          </Fragment>
        );
      })}
      {!!keyword && !!get(filteredOrderedFeatures, 'length') && clearFilterButton}
      {isUsed && (
        <Row className="justify-content-center mt-2 mt-md-0 mb-2">
          <Col xs={12} lg={9}>
            <TrackCarValueModule btnColor="blue-50" />
          </Col>
        </Row>
      )}
      <div className="text-center mb-3_5">
        <InventorySection
          visitorLocation={visitorLocation}
          vehicle={vehicle}
          styleId={styleId}
          trimName={trimName}
          params={params}
          pageName={pageName}
        />
      </div>
    </Fragment>
  );
}

FeaturesSpecsTableBelowFoldUI.propTypes = {
  vehicle: VehicleEntities.MakeModelSubmodelYear.isRequired,
  params: FeaturesSpecsParams.isRequired,
  style: FeatureSpecsEntities.FeatureStyleEntity,
  trimName: PropTypes.string,
  evTestedData: VehicleEntities.StyleEvTestedData,
  fuelCostsData: VehicleEntities.StyleFuelCostData,
  visitorLocation: VisitorEntities.Location,
  pageName: PropTypes.string,
  isMobile: PropTypes.bool,
};

FeaturesSpecsTableBelowFoldUI.defaultProps = {
  style: null,
  className: null,
  trimName: null,
  evTestedData: null,
  fuelCostsData: null,
  visitorLocation: null,
  pageName: null,
  isMobile: false,
};

const FeaturesSpecsTableBelowFoldContent = connectToModel(FeaturesSpecsTableBelowFoldUI, {
  evTestedData: bindToPath(
    ({ style, params }) =>
      params.year >= EV_TESTED_DATA_START_DATE &&
      !!get(style, 'styleAttributes.electric') &&
      buildEvTestedDataPath([style.id]),
    VehicleModel,
    stylesData => get(stylesData, '[0]')
  ),
});

const FeaturesSpecsTableBelowFold = connectToModel(FeaturesSpecsTableBelowFoldContent, {
  style: bindToPath(({ styleId }) => styleId && FeatureSpecsPaths.buildStyleFeaturesPath(styleId), VehicleModel),
  fuelCostsData: bindToPath(({ styleId }) => buildFuelCostsPath(styleId), VehicleModel),
});

export function FeaturesSpecsTable({
  className,
  vehicle,
  params,
  styleId,
  trimName,
  visitorLocation,
  pageName,
  isMobile,
}) {
  return (
    <Row className={classnames('features-specs-table', className)}>
      <Col xs={12} md={8}>
        <FeaturesSpecsTableAboveFold params={params} styleId={styleId} isMobile={isMobile} />
        <FeaturesSpecsTableBelowFold
          vehicle={vehicle}
          params={params}
          styleId={styleId}
          isMobile={isMobile}
          visitorLocation={visitorLocation}
          trimName={trimName}
          pageName={pageName}
        />
      </Col>
    </Row>
  );
}

FeaturesSpecsTable.propTypes = {
  vehicle: VehicleEntities.MakeModelSubmodelYear.isRequired,
  params: FeaturesSpecsParams.isRequired,
  styleId: PropTypes.number,
  className: PropTypes.string,
  trimName: PropTypes.string,
  visitorLocation: VisitorEntities.Location,
  pageName: PropTypes.string,
  isMobile: PropTypes.bool,
};

FeaturesSpecsTable.defaultProps = {
  styleId: null,
  className: null,
  trimName: null,
  evTestedData: null,
  fuelCostsData: null,
  visitorLocation: null,
  pageName: null,
  isMobile: false,
};
